import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, ArrowLeft, ArrowRight } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { getActualites, getActualite } from "@/lib/api";
import { IMAGES } from "@/lib/site";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });

export function Actualites() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getActualites()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="actualites-page">
      <PageHero
        eyebrow="Actualités"
        title="La vie au lycée Joseph Wresinski"
        subtitle="Sorties, projets pédagogiques, réussites d'élèves et évènements : retrouvez toute l'actualité de notre établissement."
        image={IMAGES.classroom}
      />

      <Section>
        {loading ? (
          <p className="text-muted-foreground">Chargement…</p>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground">Aucune actualité pour le moment.</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((a, i) => (
              <Link
                key={a.slug}
                to={`/actualites/${a.slug}`}
                data-testid={`actualite-${a.slug}`}
                className={`group flex flex-col overflow-hidden rounded-3xl border border-black/5 bg-brand-cream transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                  i === 0 ? "md:col-span-2 md:row-span-1" : ""
                }`}
              >
                <div className={`relative overflow-hidden ${i === 0 ? "aspect-[16/8]" : "aspect-[16/10]"}`}>
                  <img
                    src={a.image}
                    alt={a.titre}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-brand-cream/90 px-3 py-1 text-xs font-semibold text-brand-slate-dark backdrop-blur">
                    {a.categorie}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(a.date)}
                  </div>
                  <h3 className="mt-3 font-heading text-xl font-bold leading-tight text-brand-slate-dark">
                    {a.titre}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{a.extrait}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-slate transition-colors group-hover:text-brand-sand-dark">
                    Lire la suite <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

export function ActualiteDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getActualite(slug)
      .then(setItem)
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <p className="mx-auto max-w-7xl px-5 py-20 text-muted-foreground">Chargement…</p>;
  if (!item) {
    return (
      <Section title="Article introuvable">
        <Link to="/actualites" className="text-brand-slate hover:underline">
          ← Retour aux actualités
        </Link>
      </Section>
    );
  }

  return (
    <div data-testid={`actualite-detail-${slug}`}>
      <PageHero eyebrow={item.categorie} title={item.titre} subtitle={formatDate(item.date)} image={item.image}>
        <Link
          to="/actualites"
          data-testid="back-to-actualites"
          className="inline-flex items-center gap-2 text-sm font-medium text-brand-cream/80 transition-colors hover:text-brand-sand"
        >
          <ArrowLeft className="h-4 w-4" /> Toutes les actualités
        </Link>
      </PageHero>

      <Section>
        <div className="mx-auto max-w-3xl">
          <p className="text-lg leading-relaxed text-muted-foreground">{item.extrait}</p>
          <div className="mt-8 space-y-5 text-base leading-relaxed text-brand-slate-dark">
            {item.contenu.split("\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}
